import { useEffect, useMemo, useState } from "react";
import { Public, Wallet, Me, getAuth, api } from "@/lib/api";
import { Link } from "@/lib/router";
import { toast } from "@/components/ui/dialog";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Lock, ExternalLink, Calendar, Crown, FileText, FileArchive, Download,
  Building2, MapPin, Clock, Eye, Share2, Copy, Check, Loader2, Layers,
} from "lucide-react";
import { marked } from "marked";
import { Skeleton } from "@/components/ui/skeleton";
import { Pagination } from "@/components/Pagination";
import { QuickSubscribeButton } from "@/components/QuickSubscribeButton";
import { enrichServiceFeeTable } from "@/lib/serviceFeeTable";

marked.setOptions({ gfm: true, breaks: true });

function looksLikeHtml(s: string) {
  return /<\/?[a-z][\s\S]*>/i.test(s);
}
function looksLikeMarkdown(s: string) {
  return /(^|\n)\s*#{1,6}\s/.test(s) || /(^|\n)\s*[-*+]\s/.test(s) || /\[[^\]]+\]\([^)]+\)/.test(s);
}
function renderRich(content: string): string {
  if (!content) return "";
  // 已经是 HTML 且无 markdown 语法痕迹：直接返回
  if (looksLikeHtml(content) && !looksLikeMarkdown(content)) return content;
  // 否则一律走 marked（marked 默认会把 HTML 原样保留并解析其中的 markdown）
  return marked.parse(content, { async: false }) as string;
}

function fmtBytes(n?: number | null) {
  if (!n) return "—";
  const u = ["B", "KB", "MB", "GB"];
  let i = 0; let v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}
function fileIcon(mime?: string | null, name?: string) {
  const n = (name || "").toLowerCase();
  if ((mime || "").includes("zip") || n.endsWith(".zip") || n.endsWith(".rar") || n.endsWith(".7z")) return FileArchive;
  return FileText;
}

const FIELD_LABELS: Record<string, string> = {
  year: "年份", province: "省份", bidder: "招标方", winner: "中标人",
  amount: "金额", category: "采购类型", pur_type: "采购类型", type: "公告类型",
};

export function ProjectDetailPage({ slug }: { slug: string }) {
  const [p, setP] = useState<any | null>(null);
  const [err, setErr] = useState<string>("");
  const [copied, setCopied] = useState(false);
  const [me, setMe] = useState<any>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  useEffect(() => {
    setP(null); setErr("");
    Public.project(slug).then(setP).catch((e) => setErr(e.message));
    if (getAuth()) Me.profile().then(setMe).catch(() => setMe(null));
    window.scrollTo({ top: 0 });
  }, [slug]);

  const topMembership = useMemo(() => {
    if (!me) return null;
    const ms: any[] = Array.isArray(me.memberships) ? me.memberships : [];
    const active = ms.filter((m) => !m.expires_at || new Date(m.expires_at) > new Date());
    if (active.length === 0) return null;
    return active.find((m) => !m.expires_at) ||
      active.slice().sort((a, b) => new Date(b.expires_at).getTime() - new Date(a.expires_at).getTime())[0];
  }, [me]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(location.href);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const html = useMemo(() => p ? enrichServiceFeeTable(renderRich(p.content_html || "")) : "", [p]);

  if (err) return <div className="container mx-auto px-4 py-16 text-center">
    <div className="text-3xl font-bold text-muted-foreground">{err.includes("not_found") ? "404" : "出错了"}</div>
    <p className="text-sm text-muted-foreground mt-2">{err}</p>
    <Link to="/" className="inline-block mt-4"><Button variant="outline">返回首页</Button></Link>
  </div>;
  if (!p) return (
    <div>
      {/* hero 骨架 */}
      <section className="border-b bg-gradient-to-b from-primary/[0.06] to-transparent">
        <div className="container mx-auto px-4 py-8 md:py-10 max-w-6xl space-y-3">
          <Skeleton className="h-3 w-40" />
          <Skeleton className="h-8 w-3/4" />
          <Skeleton className="h-4 w-2/3" />
          <div className="flex gap-3 pt-2">
            <Skeleton className="h-3 w-20" />
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-3 w-16" />
          </div>
        </div>
      </section>
      <div className="container mx-auto px-4 py-8 max-w-6xl grid lg:grid-cols-[1fr_300px] gap-8">
        <div className="space-y-4">
          <Skeleton className="h-28 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-48 w-full" />
        </div>
      </div>
    </div>
  );

  const locked = p.paywall?.locked;
  const lockReason: string = p.paywall?.reason || "membership";
  const df: Record<string, any> = p.dynamic_fields || {};
  const isWinner = ["中标公告", "中标候选人"].includes(df.type);
  const attachments: any[] = Array.isArray(p.attachments) ? p.attachments : [];
  const related: any[] = Array.isArray(p.related) ? p.related : [];

  const metaItems: Array<[string, any]> = ([
    ["发布时间", p.published_at ? new Date(p.published_at).toLocaleString() : null],
    ["所属栏目", p.category?.name],
    ["招标方", df.bidder],
    ["地区", df.province],
    ["年份", df.year],
    ["公告类型", df.type],
    ...(isWinner ? [["中标人", df.winner]] : []),
    ...(df.amount ? [["合同金额", df.amount]] : []),
  ] as Array<[string, any]>).filter(([, v]) => v !== undefined && v !== null && v !== "");

  return (
    <div>
      {/* Hero header */}
      <section className="border-b bg-gradient-to-b from-primary/[0.06] to-transparent">
        <div className="container mx-auto px-4 py-8 md:py-10 max-w-6xl">
          <div className="text-sm text-muted-foreground mb-3 flex items-center flex-wrap gap-1">
            <Link to="/" className="hover:text-foreground">首页</Link>
            <span>/</span>
            <Link to={`/c/${p.category.slug}`} className="hover:text-foreground">{p.category.name}</Link>
            {isWinner && <>
              <span>/</span>
              <Link to="/winners" className="hover:text-foreground">中标快讯</Link>
            </>}
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            {df.type && (
              <Badge className={isWinner ? "bg-amber-500 hover:bg-amber-500/90" : ""} variant={isWinner ? "default" : "secondary"}>
                {df.type}
              </Badge>
            )}
            {df.category && <Badge variant="outline">{df.category}</Badge>}
            {locked && <Badge variant="outline" className="border-amber-500/50 text-amber-700"><Lock className="h-3 w-3 mr-1" />会员可见</Badge>}
            {!locked && topMembership && (
              <Badge className="bg-gradient-to-r from-amber-400 to-orange-500 text-white border-0 hover:from-amber-400 hover:to-orange-500">
                <Crown className="h-3 w-3 mr-1" />VIP 免费查看
              </Badge>
            )}
          </div>
          <h1 className="text-2xl md:text-3xl font-bold tracking-tight leading-snug">{p.title}</h1>
          {p.summary && <p className="text-muted-foreground mt-3 leading-relaxed max-w-4xl">{p.summary}</p>}

          <div className="flex flex-wrap items-center gap-4 mt-5 text-sm text-muted-foreground">
            {p.published_at && (
              <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{new Date(p.published_at).toLocaleDateString()}</span>
            )}
            {df.province && <span className="inline-flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" />{df.province}</span>}
            {df.bidder && <span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />{df.bidder}</span>}
            {p.view_count != null && (
              <span className="inline-flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" />{p.view_count} 次浏览</span>
            )}
            <div className="ml-auto flex items-center gap-2">
              {p.source_url && (
                <a href={p.source_url} target="_blank" rel="noreferrer">
                  <Button variant="outline" size="sm" className="h-8"><ExternalLink className="h-3.5 w-3.5 mr-1" />原文</Button>
                </a>
              )}
              <Button variant="outline" size="sm" className="h-8" onClick={copyLink}>
                {copied ? <><Check className="h-3.5 w-3.5 mr-1" />已复制</> : <><Share2 className="h-3.5 w-3.5 mr-1" />分享</>}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <div className="container mx-auto px-4 py-8 max-w-6xl grid lg:grid-cols-[1fr_300px] gap-8">
        {/* 主体 */}
        <article className="min-w-0">
          {/* VIP 身份提示横幅 */}
          {!locked && topMembership && (
            <div className="mb-6 rounded-lg border border-amber-200/70 bg-gradient-to-r from-amber-50 via-orange-50 to-amber-50 dark:border-amber-900/40 dark:from-amber-950/30 dark:to-orange-950/30 px-5 py-4 flex items-center gap-4">
              <div className="shrink-0 inline-flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-sm">
                <Crown className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-amber-900 dark:text-amber-200">
                  VIP 免费查看 · 您正享受{topMembership.name}权益
                </div>
                <div className="text-xs text-amber-800/80 dark:text-amber-300/70 mt-0.5">
                  {topMembership.expires_at
                    ? `有效期至 ${new Date(topMembership.expires_at).toLocaleDateString()}，期间所有付费内容免费畅览`
                    : "终身会员权益已生效，全站付费内容永久免费畅览"}
                </div>
              </div>
              <Link to="/me" className="hidden sm:block shrink-0">
                <Button variant="outline" size="sm" className="border-amber-300 text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:text-amber-200">我的会员</Button>
              </Link>
            </div>
          )}
          {/* 关键信息卡 */}
          {metaItems.length > 0 && (
            <Card className="mb-6">
              <CardContent className="p-5">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3">项目关键信息</div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
                  {metaItems.map(([label, value]) => (
                    <div key={label}>
                      <div className="text-xs text-muted-foreground">{label}</div>
                      <div className="font-medium text-sm mt-0.5 break-all">{String(value)}</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* 正文 */}
          {locked ? (
            <Card className="border-amber-200/60 bg-amber-50/50 dark:bg-amber-900/10">
              <CardContent className="p-10 text-center space-y-4">
                <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/40">
                  <Lock className="h-7 w-7" />
                </div>
                {lockReason === "login" ? (
                  <>
                    <h3 className="text-xl font-semibold">登录后查看完整内容</h3>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      本公告需要登录后查看完整内容、附件与中标人详情。
                    </p>
                    <Link to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`}>
                      <Button size="lg" className="mt-2">立即登录</Button>
                    </Link>
                  </>
                ) : (
                  <>
                    <h3 className="text-xl font-semibold">本内容需要 VIP 会员查看</h3>
                    <p className="text-sm text-muted-foreground max-w-md mx-auto">
                      开通 {(p.paywall.required_plans || []).join(" / ") || "VIP"} 会员，立即解锁本公告完整内容、附件下载与中标人详情。
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center mt-2">
                      <Link to="/pricing">
                        <Button size="lg"><Crown className="mr-2 h-4 w-4" /> 立即开通</Button>
                      </Link>
                      <PurchaseResourceButton
                        projectId={p.id}
                        purchase={p.paywall.purchase}
                        onUnlocked={() => Public.project(slug).then(setP).catch(() => {})}
                      />
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-6 md:p-8">
                {Array.isArray(p.dynamic_fields?.packages) && p.dynamic_fields.packages.length > 0 ? (
                  <PackagesView df={p.dynamic_fields} />
                ) : (
                  <>
                    <div className="overflow-x-auto">
                      <div
                        className="prose prose-rich prose-neutral dark:prose-invert max-w-none prose-headings:scroll-mt-20 prose-h2:text-xl prose-h2:border-l-4 prose-h2:border-primary prose-h2:pl-3 prose-h2:py-1 prose-h2:bg-muted/40 prose-h2:rounded-r prose-h3:text-base prose-table:text-sm prose-img:rounded-lg"
                        dangerouslySetInnerHTML={{ __html: html }}
                      />
                    </div>
                    {!html && (
                      <div className="text-sm text-muted-foreground py-8 text-center">暂无正文内容</div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          )}

          {/* 物资类标段 */}
          {Array.isArray(df.bid_sections) && df.bid_sections.length > 0 && !locked && (
            <Card className="mt-6">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Layers className="h-4 w-4 text-primary" /> 标段清单
                    <span className="text-xs font-normal text-muted-foreground">
                      共 {df.bid_sections_count ?? df.bid_sections.length} 个标段
                    </span>
                  </h3>
                </div>
                <ol className="grid sm:grid-cols-2 gap-2 list-decimal list-inside text-sm">
                  {df.bid_sections.map((name: string, i: number) => (
                    <li key={i} className="px-2 py-1.5 rounded bg-muted/40 leading-snug flex items-center justify-between gap-2">
                      <span className="text-muted-foreground flex-1 min-w-0 truncate" title={name}>{name}</span>
                      <QuickSubscribeButton keyword={name} categoryId={p.category?.id} />
                    </li>
                  ))}
                </ol>
                <div className="text-xs text-muted-foreground mt-3">
                  数据来自招标公告 zip 内「货物清单*.xlsx」的工作表名（自动解析）。点击右侧「+ 订阅」可一键监控该标段后续动态。
                </div>
              </CardContent>
            </Card>
          )}

          {/* 附件 */}
          {attachments.length > 0 && (
            <Card className="mt-6">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold flex items-center gap-2">
                    <Download className="h-4 w-4 text-primary" /> 附件下载
                    <span className="text-xs font-normal text-muted-foreground">{attachments.length} 个文件</span>
                  </h3>
                  {locked && <Badge variant="outline" className="text-amber-700 border-amber-500/50"><Lock className="h-3 w-3 mr-1" />会员可下载</Badge>}
                </div>
                <ul className="divide-y rounded-md border">
                  {attachments.map((a) => {
                    const Icon = fileIcon(a.mime, a.filename);
                    const ext = (a.filename || "").split(".").pop()?.toUpperCase() || "FILE";
                    return (
                      <li key={a.id} className="flex items-center gap-3 px-4 py-3 hover:bg-muted/40 transition">
                        <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary shrink-0">
                          <Icon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{a.filename}</div>
                          <div className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                            <span className="uppercase">{ext}</span>
                            <span>·</span>
                            <span>{fmtBytes(a.size_bytes)}</span>
                          </div>
                        </div>
                        {a.download_url ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 min-w-[76px]"
                            disabled={downloadingId === a.id}
                            onClick={async () => {
                              if (downloadingId) return;
                              setDownloadingId(a.id);
                              try {
                                // 走 ?as=json：当前页面 fetch 带 Authorization 头拿到真实地址，
                                // 再 window.open，避免 <a target="_blank"> 新窗口丢失登录态被 paywall 判 403。
                                const raw = a.download_url || "";
                                const path = raw.startsWith("/api/") ? raw.slice(4) : raw;
                                const data = await api<{ url: string; filename?: string }>(
                                  `${path}${path.includes("?") ? "&" : "?"}as=json`
                                );
                                if (data?.url) {
                                  window.open(data.url, "_blank", "noopener,noreferrer");
                                } else {
                                  toast.error("下载失败：未获取到下载地址");
                                }
                              } catch (e: any) {
                                toast.error(`下载失败：${e?.message || "请稍后再试"}`);
                              } finally {
                                setDownloadingId(null);
                              }
                            }}
                          >
                            {downloadingId === a.id ? (
                              <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />获取中</>
                            ) : (
                              <><Download className="h-3.5 w-3.5 mr-1" />下载</>
                            )}
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" className="h-8" disabled><Lock className="h-3.5 w-3.5 mr-1" />会员</Button>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </CardContent>
            </Card>
          )}
        </article>

        {/* 侧边栏 */}
        <aside className="space-y-5 lg:sticky lg:top-20 self-start">
          {/* 快速操作 */}
          <Card>
            <CardContent className="p-4 space-y-2.5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">快速操作</div>
              {p.source_url && (
                <a href={p.source_url} target="_blank" rel="noreferrer" className="block">
                  <Button variant="outline" className="w-full justify-start"><ExternalLink className="h-4 w-4 mr-2" />查看原文</Button>
                </a>
              )}
              <Button variant="outline" className="w-full justify-start" onClick={copyLink}>
                {copied ? <><Check className="h-4 w-4 mr-2" />链接已复制</> : <><Copy className="h-4 w-4 mr-2" />复制链接</>}
              </Button>
              {locked && (
                <Link to="/pricing" className="block">
                  <Button className="w-full justify-start"><Crown className="h-4 w-4 mr-2" />开通 VIP</Button>
                </Link>
              )}
            </CardContent>
          </Card>

          {/* 相关推荐 */}
          {related.length > 0 && (
            <Card>
              <CardContent className="p-4">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
                  <Clock className="h-3 w-3" /> 同栏目最新
                </div>
                <ul className="space-y-3">
                  {related.map((r) => (
                    <li key={r.slug}>
                      <Link to={`/p/${r.slug}`} className="text-sm font-medium hover:text-primary line-clamp-2 leading-snug">
                        {r.title}
                      </Link>
                      <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {r.published_at ? new Date(r.published_at).toLocaleDateString() : "—"}
                        {r?.dynamic_fields?.province && <><span>·</span><span>{r.dynamic_fields.province}</span></>}
                      </div>
                    </li>
                  ))}
                </ul>
                <Link to={`/c/${p.category.slug}`} className="block">
                  <Button variant="ghost" size="sm" className="w-full mt-3">查看更多 →</Button>
                </Link>
              </CardContent>
            </Card>
          )}
        </aside>
      </div>
    </div>
  );
}

function PurchaseResourceButton({ projectId, purchase, onUnlocked }: { projectId: string; purchase: any; onUnlocked?: () => void }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const buy = async () => {
    if (loading) return;
    setLoading(true); setErr("");
    try {
      const r = await Wallet.purchaseProject(projectId);
      if (r?.already) {
        toast.info("你已解锁此资源");
      } else {
        const delta = r?.delta ?? 0;
        const balance = r?.balance;
        const balanceText = typeof balance === "number" ? `，当前余额 ${balance}` : "";
        toast.success(`解锁成功 ${delta} 积分${balanceText}`);
      }
      // 不整页刷新，只重拉项目详情重新渲染解锁后的内容
      onUnlocked?.();
    } catch (e: any) {
      if (e?.status === 401) { location.href = "/login"; return; }
      const msg = e?.body?.message || "购买失败";
      setErr(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };
  if (!purchase || !purchase.points) return null;
  return (
    <div className="flex flex-col items-center gap-1">
      <Button size="lg" variant="outline" disabled={loading} onClick={buy}>
        {loading ? "处理中..." : `${purchase.points} 积分解锁`}
      </Button>
      {err && <div className="text-xs text-destructive">{err}</div>}
    </div>
  );
}

type PkgRow = { pack: string; product: string; company: string; price: number | null };

function PackagesView({ df }: { df: any }) {
  const packages: Array<{ pack_num: any; items: any[] }> = Array.isArray(df?.packages) ? df.packages : [];
  const winners: string[] = Array.isArray(df?.winners) ? df.winners : [];
  const rows = useMemo<PkgRow[]>(() => {
    const out: PkgRow[] = [];
    for (const pkg of packages) {
      const items = Array.isArray(pkg.items) ? pkg.items : [];
      for (const it of items) {
        if (Array.isArray(it)) {
          out.push({ pack: String(pkg.pack_num ?? ""), company: String(it[0] ?? ""), product: String(it[1] ?? ""), price: typeof it[2] === "number" ? it[2] : null });
        } else if (it && typeof it === "object") {
          out.push({ pack: String(pkg.pack_num ?? ""), company: String(it.company ?? ""), product: String(it.product ?? ""), price: typeof it.price === "number" ? it.price : null });
        }
      }
    }
    return out;
  }, [packages]);

  const PAGE_SIZE = 50;
  const [page, setPage] = useState(1);
  const [kw, setKw] = useState("");
  const [packFilter, setPackFilter] = useState<string>("all");

  const packOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) set.add(r.pack);
    return Array.from(set);
  }, [rows]);

  const filtered = useMemo(() => {
    const k = kw.trim().toLowerCase();
    return rows.filter((r) => {
      if (packFilter !== "all" && r.pack !== packFilter) return false;
      if (k && !(r.company.toLowerCase().includes(k) || r.product.toLowerCase().includes(k))) return false;
      return true;
    });
  }, [rows, kw, packFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const slice = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // 过滤条件变化时回到第一页
  useEffect(() => { setPage(1); }, [kw, packFilter]);

  const fmtPrice = (n: number | null) => {
    if (n === null || n === undefined || isNaN(n)) return "—";
    const s = n.toFixed(4).replace(/0+$/, "").replace(/\.$/, "");
    return s;
  };

  const totalAmount = typeof df?.amount_wan === "number" ? df.amount_wan : null;

  return (
    <div className="space-y-4">
      <div className="text-sm leading-relaxed">
        该项目共 <strong>{packages.length}</strong> 个分包，
        <strong>{winners.length}</strong> 家中标企业，
        中标总金额合计 <strong>{totalAmount !== null ? fmtPrice(totalAmount) : "—"} 万元</strong>。
        <span className="text-muted-foreground">共 {rows.length} 条明细</span>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <input
          value={kw}
          onChange={(e) => setKw(e.target.value)}
          placeholder="搜索企业 / 物料"
          className="h-9 px-3 text-sm rounded-md border bg-background flex-1 min-w-[180px] max-w-xs outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
        />
        {packOptions.length > 1 && (
          <select
            value={packFilter}
            onChange={(e) => setPackFilter(e.target.value)}
            className="h-9 px-2 text-sm rounded-md border bg-background"
          >
            <option value="all">全部分包</option>
            {packOptions.map((p) => <option key={p} value={p}>分包 {p}</option>)}
          </select>
        )}
        <div className="text-xs text-muted-foreground ml-auto">
          {filtered.length === rows.length ? `共 ${rows.length} 条` : `筛选 ${filtered.length} / ${rows.length} 条`}
        </div>
      </div>

      <div className="rounded-md border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-left">
              <th className="px-3 py-2 font-medium w-16">分包</th>
              <th className="px-3 py-2 font-medium">物料</th>
              <th className="px-3 py-2 font-medium">中标企业</th>
              <th className="px-3 py-2 font-medium text-right w-32">中标价(万元)</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {slice.map((r, i) => (
              <tr key={`${r.pack}-${i}`} className="hover:bg-muted/30 align-top">
                <td className="px-3 py-2 text-muted-foreground">{r.pack}</td>
                <td className="px-3 py-2 break-words">{r.product || "—"}</td>
                <td className="px-3 py-2 break-words">{r.company || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">{fmtPrice(r.price)}</td>
              </tr>
            ))}
            {slice.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-10 text-center text-sm text-muted-foreground">没有匹配的明细</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {filtered.length > PAGE_SIZE && (
        <Pagination
          page={safePage}
          total={filtered.length}
          pageSize={PAGE_SIZE}
          onChange={setPage}
        />
      )}
    </div>
  );
}
